import React from 'react';
import { useCompanyOffers } from "../../Hooks/useCompanyOffers";
import { Loader2, FileText, Clock, DollarSign, User, Briefcase, FileSignature, Receipt } from "lucide-react";
import { format } from "date-fns";

export default function UserAgreement() {
    const { allOffers, loadingAll } = useCompanyOffers();

    const getAgreementLink = (path) => {
        if (!path) return null;
        return `${path}`;
    };

    if (loadingAll) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="flex flex-col items-center gap-4">
                    <Loader2 className="w-12 h-12 text-[#1665f6] animate-spin" />
                    <p className="text-gray-500 font-medium animate-pulse">Loading agreements...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-[1400px] mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <div className="relative overflow-hidden bg-white p-8 rounded-[2rem] border border-gray-100 shadow-sm border-b-4 border-b-[#1665f6]/20">
                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="space-y-2">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 bg-[#1665f6]/10 rounded-xl">
                                <FileSignature className="w-6 h-6 text-[#1665f6]" />
                            </div>
                            <h1 className="text-3xl font-black text-gray-900 tracking-tight">User Agreements</h1>
                        </div>
                        <p className="text-gray-500 font-medium text-lg ml-1">
                            Review and download signed contracts and payment statuses
                        </p>
                    </div>
                    <div className="flex items-center gap-4 bg-gray-50 p-4 rounded-2xl border border-gray-100">
                        <div className="text-right">
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Total Agreements</p>
                            <p className="text-2xl font-black text-gray-900">{allOffers.length}</p>
                        </div>
                        <div className="w-px h-10 bg-gray-200" />
                        <div className="p-2 bg-white rounded-xl shadow-sm">
                            <Receipt className="w-6 h-6 text-[#8830b8]" />
                        </div>
                    </div>
                </div>

                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#1665f6]/5 to-transparent rounded-full -mr-32 -mt-32 blur-3xl" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-[#8830b8]/5 to-transparent rounded-full -ml-24 -mb-24 blur-2xl" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
                {allOffers.map((offer) => (
                    <div
                        key={offer.id}
                        className="group bg-white rounded-[2rem] border border-gray-100 shadow-sm hover:shadow-xl hover:shadow-[#1665f6]/5 hover:-translate-y-1 transition-all duration-500 overflow-hidden flex flex-col"
                    >
                        <div className="p-6 pb-0 flex justify-between items-start">
                            <div className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.1em] ${offer.payment_status === 'paid'
                                    ? 'bg-emerald-50 text-emerald-600 border border-emerald-100 shadow-sm shadow-emerald-100/50'
                                    : 'bg-amber-50 text-amber-600 border border-amber-100 shadow-sm shadow-amber-100/50'
                                }`}>
                                <div className={`w-1.5 h-1.5 rounded-full ${offer.payment_status === 'paid' ? 'bg-emerald-500' : 'bg-amber-500'} animate-pulse`} />
                                {offer.payment_status}
                            </div>
                            <div className="text-right">
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Agreement ID</p>
                                <p className="text-xs font-black text-gray-800">#{offer.id}</p>
                            </div>
                        </div>

                        <div className="p-6 space-y-6 flex-1">
                            <div>
                                <h3 className="text-xl font-black text-gray-900 leading-tight group-hover:text-[#1665f6] transition-colors line-clamp-1">
                                    {offer.quote?.quote_number || `Quote #${offer.quote_id}`}
                                </h3>
                                <div className="flex items-center gap-2 mt-2">
                                    <div className="p-1 bg-gray-50 rounded-md">
                                        <Clock className="w-3 h-3 text-gray-400" />
                                    </div>
                                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                                        {format(new Date(offer.created_at), 'MMMM dd, yyyy')}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center justify-between p-4 bg-gray-50/50 rounded-2xl border border-gray-100/50 relative overflow-hidden group-hover:bg-white transition-colors">
                                <div className="space-y-1 relative z-10">
                                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-1">
                                        <DollarSign className="w-3 h-3" />
                                        Agreed Price
                                    </p>
                                    <p className="text-2xl font-black text-gray-900 tracking-tighter">
                                        ${parseFloat(offer.price).toLocaleString()}
                                    </p>
                                </div>
                                <div className="text-right relative z-10">
                                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">Offer Status</p>
                                    <span className={`px-3 py-1 rounded-lg text-xs font-black uppercase tracking-wider ${offer.status === 'accepted' ? 'text-emerald-600 bg-emerald-50' : 'text-blue-600 bg-blue-50'
                                        }`}>
                                        {offer.status}
                                    </span>
                                </div>
                                <div className="absolute top-0 right-0 w-16 h-16 bg-[#1665f6]/5 blur-xl rounded-full -mr-8 -mt-8" />
                            </div>

                            <div className="space-y-3">
                                <div className="flex items-center gap-4 group/item">
                                    <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center border border-gray-100 group-hover/item:border-[#1665f6]/20 group-hover/item:shadow-sm transition-all">
                                        <User className="w-5 h-5 text-gray-400 group-hover/item:text-[#1665f6]" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Client Name</p>
                                        <p className="text-sm font-bold text-gray-800">{offer.quote?.first_name} {offer.quote?.last_name}</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-4 group/item">
                                    <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center border border-gray-100 group-hover/item:border-[#8830b8]/20 group-hover/item:shadow-sm transition-all">
                                        <Briefcase className="w-5 h-5 text-gray-400 group-hover/item:text-[#8830b8]" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Type</p>
                                        <p className="text-sm font-bold text-gray-800 capitalize">{offer.agreement_type || 'N/A'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-6 pt-0 mt-auto">
                            {offer.user_agreement ? (
                                <a
                                    href={getAgreementLink(offer.user_agreement)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full h-12 bg-gray-900 text-white rounded-xl shadow-lg shadow-gray-200 hover:shadow-gray-300 hover:bg-black transition-all duration-300 font-bold text-sm flex items-center justify-center gap-2 group-hover:bg-[#1665f6] group-hover:shadow-[#1665f6]/20"
                                >
                                    <FileText className="w-4 h-4" />
                                    View signed agreement
                                </a>
                            ) : (
                                <div className="w-full h-12 bg-gray-50 border border-gray-200 rounded-xl text-gray-400 font-bold text-sm flex items-center justify-center gap-2 cursor-not-allowed">
                                    <FileText className="w-4 h-4 opacity-50" />
                                    Pending Signature
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {allOffers.length === 0 && (
                <div className="flex flex-col items-center justify-center py-32 bg-white rounded-[3rem] border border-gray-100 shadow-sm">
                    <div className="relative mb-6">
                        <div className="absolute inset-0 bg-[#1665f6]/10 blur-3xl rounded-full" />
                        <div className="relative p-8 bg-gray-50 rounded-full">
                            <FileSignature className="w-20 h-20 text-gray-300" />
                        </div>
                    </div>
                    <h3 className="text-2xl font-black text-gray-900 mb-2">No Agreements Found</h3>
                    <p className="text-gray-500 font-medium">Any accepted offers will appear here automatically.</p>
                </div>
            )}
        </div>
    );
}
